import React, { useState } from'react';
import { useNavigate, useSearchParams } from'react-router-dom';
import { useLayout } from'../components/Layout';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import DataTable from'../components/tables/DataTable';
import BarChart from'../components/charts/BarChart';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader, { InlineLoader } from'../components/PageLoader';

export default function UserActivity() {
 const { timeRange } = useLayout();
 const [searchParams] = useSearchParams();
 // Allow deep-linking to a specific user (e.g. from the dashboard's
 // "Top Users by Usage" chart → /users?user=jane@contoso.com).
 // A selection is either a human user (by email) or an application caller
 // (by appClientId). We track both the id and its kind so the timeline
 // below knows which endpoint to call.
 const [selected, setSelected] = useState(
   searchParams.get('user')
     ? { kind: 'user', id: searchParams.get('user'), label: searchParams.get('user') }
     : null
 );
 const navigate = useNavigate();

 const { data: users, isPending: usersPending } = useApiQuery(
 ['users', timeRange],
 () => api.getUsers(timeRange),
 { refetchInterval: 60000 }
 );

 // Auth-method breakdown — how the gateway is actually being accessed. This
 // is the enterprise-meaningful context that explains why the Users table
 // below is user-token-only: app-token / API-key traffic carries no human
 // identity and appears in the Application Callers table instead.
 const { data: authBreakdown, isPending: authPending } = useApiQuery(
 ['auth-breakdown', timeRange],
 () => api.getAuthBreakdown(timeRange),
 { refetchInterval: 60000 }
 );

 // Application callers — the app-token / API-key traffic, keyed on the Entra
 // app (resolved to a friendly name server-side via Graph).
 const { data: apps, isPending: appsPending } = useApiQuery(
 ['app-callers', timeRange],
 () => api.getAppCallers(timeRange),
 { refetchInterval: 60000 }
 );

 // Top Users by Usage — moved here from the Executive Dashboard. Sourced from
 // the same ai-gateway traces that power the users table, ranked by call
 // volume. Clicking a bar selects that user and loads their timeline below.
 const { data: topUsers, isPending: topUsersPending } = useApiQuery(
 ['top-users', timeRange],
 () => api.getTopUsers(timeRange, 10),
 { refetchInterval: 60000 }
 );

 const topUsersData = React.useMemo(() => (
 (Array.isArray(topUsers) ? topUsers : []).map((u) => ({
 name:
 (u.UserName && u.UserName.trim()) ||
 (u.UserEmail && u.UserEmail.trim()) ||
 'Unknown user',
 Calls: Number(u.Calls) || 0,
 Tokens: Number(u.TotalTokens) || 0,
 UserEmail: u.UserEmail,
 }))
 ), [topUsers]);

 const authTotals = React.useMemo(() => {
 const rows = Array.isArray(authBreakdown) ? authBreakdown : [];
 const total = rows.reduce((sum, r) => sum + (Number(r.Calls) || 0), 0);
 return { rows, total };
 }, [authBreakdown]);

 const handleTopUserClick = (row) => {
 if (row?.UserEmail) setSelected({ kind: 'user', id: row.UserEmail, label: row.UserEmail });
 };

 // Timeline for the current selection. Only one of the two queries is ever
 // enabled at a time, gated on the selection kind.
 const { data: userActivityData, isPending: userActivityPending } = useApiQuery(
 ['user-activity', selected?.id, timeRange],
 () => api.getUserActivity(selected.id, timeRange),
 { enabled: !!selected && selected.kind === 'user' }
 );
 const { data: appActivityData, isPending: appActivityPending } = useApiQuery(
 ['app-activity', selected?.id, timeRange],
 () => api.getAppActivity(selected.id, timeRange),
 { enabled: !!selected && selected.kind === 'app' }
 );
 const activity = selected?.kind === 'app' ? appActivityData : userActivityData;
 const activityPending = selected?.kind === 'app' ? appActivityPending : userActivityPending;

 const userColumns = [
 {
 headerName:'User',
 field:'UserEmail',
 flex: 2,
 cellRenderer: (p) => (
 <span className="font-medium text-brand-600 cursor-pointer hover:underline">
 {p.value}
 </span>
 ),
 },
 { headerName:'Name', field:'UserName', flex: 1 },
 {
 headerName:'Total Calls',
 field:'TotalCalls',
 width: 110,
 type:'numericColumn',
 valueFormatter: (p) => (parseInt(p.value) || 0).toLocaleString(),
 },
 { headerName:'Unique APIs', field:'UniqueAPIs', width: 110, type:'numericColumn' },
 {
 headerName:'First Seen',
 field:'FirstSeen',
 width: 160,
 valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString() :'—',
 },
 {
 headerName:'Last Seen',
 field:'LastSeen',
 width: 160,
 valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString() :'—',
 },
 ];

 const appColumns = [
 {
 headerName:'Application',
 field:'AppName',
 flex: 2,
 cellRenderer: (p) => {
 const id = p.data?.AppClientId;
 const label = p.value
 || (id === 'unknown' ? 'Unknown / unattributed' : id)
 || 'Unknown app';
 return (
 <span className="font-medium text-brand-600 cursor-pointer hover:underline">
 {label}
 </span>
 );
 },
 },
 { headerName:'Client ID', field:'AppClientId', flex: 2,
 cellRenderer: (p) => (
 <span className="font-mono text-xs text-gray-500">{p.value}</span>
 ),
 },
 {
 headerName:'Total Calls',
 field:'TotalCalls',
 width: 110,
 type:'numericColumn',
 valueFormatter: (p) => (parseInt(p.value) || 0).toLocaleString(),
 },
 {
 headerName:'Total Tokens',
 field:'TotalTokens',
 width: 120,
 type:'numericColumn',
 valueFormatter: (p) => (parseInt(p.value) || 0).toLocaleString(),
 },
 { headerName:'Unique APIs', field:'UniqueAPIs', width: 110, type:'numericColumn' },
 {
 headerName:'First Seen',
 field:'FirstSeen',
 width: 160,
 valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString() :'—',
 },
 {
 headerName:'Last Seen',
 field:'LastSeen',
 width: 160,
 valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString() :'—',
 },
 ];

 const activityColumns = [
 {
 headerName:'Time',
 field:'timestamp',
 width: 170,
 valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString() :'—',
 },
 { headerName:'Event', field:'Event', width: 100 },
 { headerName:'Method', field:'Method', width: 80 },
 { headerName:'URL', field:'Url', flex: 2 },
 { headerName:'Auth', field:'AuthMethod', width: 80 },
 { headerName:'IP', field:'IP', width: 130 },
 { headerName:'Correlation ID', field:'CorrelationId', width: 200,
 cellRenderer: (p) => p.value ? (
 <span
 className="text-brand-600 cursor-pointer hover:underline font-mono text-xs"
 onClick={(e) => { e.stopPropagation(); navigate(`/conversations?operationId=${encodeURIComponent(p.value)}`); }}
 >
 {p.value}
 </span>
 ) :'—',
 },
 ];

 return (
 <div className="space-y-6">
 <Breadcrumbs />
 <div>
 <h1 className="text-xl font-bold">Users &amp; Callers</h1>
 <p className="text-sm text-gray-500 mt-0.5">
 Who — and what — is calling the LLM Gateway. Human users authenticate
 with a personal (delegated) token; applications and scripts use an app
 token or API key and have no human identity.
 </p>
 </div>

 {/* Auth-method breakdown — explains the two tables below. */}
 <ErrorBoundary>
 {authPending ? (
 <PageLoader message="Loading access breakdown…" />
 ) : authTotals.rows.length > 0 ? (
 <div className="bg-white border border-gray-200 rounded-lg p-4">
 <h2 className="text-sm font-semibold text-gray-700 mb-3">
 How the gateway is being accessed
 </h2>
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 {authTotals.rows.map((r) => {
 const calls = Number(r.Calls) || 0;
 const rawPct = authTotals.total > 0 ? (calls / authTotals.total) * 100 : 0;
 // Avoid misleading rounding: a small-but-present share must never read
 // "0%" (it would contradict the call count shown below), and a dominant
 // share must not read "100%" while other traffic exists.
 let pctLabel;
 if (calls === 0) pctLabel = '0%';
 else if (rawPct > 0 && rawPct < 1) pctLabel = '<1%';
 else if (rawPct > 99 && rawPct < 100) pctLabel = '>99%';
 else pctLabel = `${Math.round(rawPct)}%`;
 const isUser = r.AuthMethod === 'User token';
 return (
 <div
 key={r.AuthMethod}
 className={`rounded-lg border p-3 ${isUser ? 'border-brand-200 bg-brand-50' : 'border-gray-200 bg-gray-50'}`}
 >
 <div className="text-xs font-medium text-gray-500">{r.AuthMethod}</div>
 <div className="text-2xl font-bold text-gray-900 mt-1">{pctLabel}</div>
 <div className="text-xs text-gray-500 mt-0.5">
 {calls.toLocaleString()} calls · {(Number(r.TotalTokens) || 0).toLocaleString()} tokens
 </div>
 </div>
 );
 })}
 </div>
 <p className="text-xs text-gray-400 mt-3">
 Only <span className="font-medium">User token</span> traffic appears in the
 Users table — those calls carry a human identity. App-token and API-key
 traffic is listed under Application Callers.
 </p>
 </div>
 ) : null}
 </ErrorBoundary>

 <ErrorBoundary>
 {topUsersPending ? (
 <PageLoader message="Loading top users…" />
 ) : topUsersData.length > 0 ? (
 <BarChart
 data={topUsersData}
 dataKeys={['Calls']}
 xAxisKey="name"
 title="Top Users by Usage"
 colorByIndex
 horizontal
 showValueLabels
 height={280}
 exportFilename="top-users"
 onBarClick={handleTopUserClick}
 />
 ) : null}
 </ErrorBoundary>

 <ErrorBoundary>
 {usersPending ? (
 <PageLoader message="Loading users…" />
 ) : (
 <DataTable
 data={users || []}
 columns={userColumns}
 title={`${(users || []).length} Active Users (user token)`}
 height={350}
 exportFilename="user-activity"
 onRowClick={(row) => setSelected({ kind: 'user', id: row.UserEmail, label: row.UserEmail })}
 />
 )}
 </ErrorBoundary>

 <ErrorBoundary>
 {appsPending ? (
 <PageLoader message="Loading application callers…" />
 ) : (
 <DataTable
 data={apps || []}
 columns={appColumns}
 title={`${(apps || []).length} Application Callers (app token / API key)`}
 height={350}
 exportFilename="app-callers"
 onRowClick={(row) => setSelected({
 kind: 'app',
 id: row.AppClientId,
 label: row.AppName || row.AppClientId,
 })}
 />
 )}
 </ErrorBoundary>

 {selected && (
 <>
 <div className="pt-2 border-t border-gray-200">
 <h2 className="text-lg font-semibold">{selected.label}</h2>
 <p className="text-sm text-gray-500">
 {selected.kind === 'app' ? 'Application activity history' : 'User activity history'}
 </p>
 </div>

 <ErrorBoundary>
 {activityPending ? (
 <InlineLoader message="Loading activity…" />
 ) : (
 <DataTable
 data={activity || []}
 columns={activityColumns}
 title="Activity Timeline"
 height={400}
 exportFilename={`${selected.kind}-${selected.id}-activity`}
 />
 )}
 </ErrorBoundary>
 </>
 )}
 </div>
 );
}
